import { ReactElement } from 'react';
import { RenderEditCellProps } from 'components/DataGrid';
import { openModal } from '@ff-it/ui';
import type { Supplier } from 'modules/supplier/supplier/types';
import { ConfirmDialog } from 'components/Actions/createConfirmDialog';

import { useEntity } from 'components';
import { BlockDetails } from 'modules/campaign/block/types';

import { trapEnterHandler } from 'components/DataGrid/editors/utils';

import eq from 'fast-deep-equal';
import { SupplierPicker } from 'modules/supplier/supplier/components/SupplierPicker';
import { GridRow } from 'modules/campaign/plan/types';
import { usePlanMutations } from 'modules/campaign/plan/state';

function checkHasValue(row: GridRow, supplier: Supplier | null): boolean {
  if (!supplier) {
    return false;
  }

  // # 1154, We can only keep values for same direction
  if (row.kind === 'SMART') {
    if (row.direction === supplier.product.direction && (row.rate.client || row.rate.provider)) {
      return true;
    }
    return false;
  }
  // @TODO RTB fields, should be on strategy?
  return (['gross_price', 'client_price', 'provider_price'] as const).some((field) => !row.config[field].empty);
}

type Props = Pick<RenderEditCellProps<GridRow, any>, 'row' | 'onClose'>;

export function EditSupplier({ row, onClose }: Props): ReactElement {
  // FIXME: this should use DialogEditor but would have to take SupplierSelector apart
  const value = row.supplier;
  const { type, id: for_block } = useEntity<BlockDetails>().item;
  const formRequest = usePlanMutations().formRequest;

  const onChange = async (supplier: Supplier | null): Promise<void> => {
    if (eq(value, supplier)) {
      onClose(false, true);
      return;
    }
    const hasValue = checkHasValue(row, supplier);

    let _keep_values = false;
    if (hasValue) {
      const res = await openModal((props) => <ConfirmDialog {...props} title="Keep values?" />, {
        testId: 'confirm-dialog',
      });
      if (typeof res === 'undefined') {
        // bail
        return;
      }
      if (res) {
        _keep_values = true;
      }
    }
    await formRequest({
      method: _keep_values ? 'PATCH' : 'PUT',
      url: `plan/rows/${row.id}/supplier/`,
      body: supplier,
    });
    onClose(false, true);
  };
  return (
    <div onKeyDown={trapEnterHandler}>
      <SupplierPicker
        value={value}
        type={type}
        kind={row.kind}
        onRequestClose={() => onClose(false, true)}
        onChange={onChange}
        for_block={for_block}
      />
    </div>
  );
}
